// THE OPTIONS — the fixed choices a `decision_needed` blocker offers, so a human
// answers by picking one instead of retyping it (#6387). Stored in
// `blockers.options` (jsonb); re-exported through `../blocker.ts`.
//
// WHY. Several raisers already ask a choice and spell it in prose — "split it, or
// fix the failures by hand?" (`gate-repeated.ts`), "Tag a baseline, or turn
// releases off?" (`release-preflight.ts`) — so the person answering retyped one of
// the options the question listed, and two answers to one question differed only
// in wording. The options are the machine-readable half of that question; the
// prose stays in `detail` for every reader that does not render them.
//
// A PICK IS STILL AN ANSWER IN WORDS. The server composes the stored `answer` from
// the chosen LABEL ({@link resolveBlockerAnswer}), so `blockers.answer`, the raw
// task an answer mints and every answer door downstream are unchanged: triage
// reads text, as it always has. Free text stays admitted on a row WITH options —
// that is the "Other" answer, and a closed set with no way out would be worse
// than the textarea it replaced.

import { z } from 'zod';
import { secretShapesInJson } from '../../value-objects/redaction';
import {
  llmPinNamesSomething,
  type TaskLlmPreference,
  TaskLlmPreferenceSchema,
} from '../../value-objects/task-envelope';

/** The stored shape's version. A literal: the day a v2 exists, v1 rows are a migration. */
export const BLOCKER_OPTIONS_VERSION = 1;
/** Fewer than two is not a choice; it is a statement. */
export const BLOCKER_OPTIONS_MIN = 2;
/**
 * More than six is a form, not a question — and the row is read in pages, so the
 * per-row bound is what keeps a page bounded (the `BLOCKER_NEEDS_MAX_FIELDS`
 * reason). Widening later is free; narrowing later makes stored rows unreadable.
 */
export const BLOCKER_OPTIONS_MAX = 6;
/** A choice's stable handle — what a door sends back. */
export const BLOCKER_OPTION_KEY_MAX = 40;
/** A choice's words in front of a human — a subject, never a dump. */
export const BLOCKER_OPTION_LABEL_MAX = 120;
/** Joins a chosen label to its note in the composed answer. */
const NOTE_SEPARATOR = ' — ';
/**
 * The human's answer cap — the ONE definition, which `../blocker.ts` re-exports.
 * It lives here because {@link BLOCKER_CHOICE_NOTE_MAX} is derived from it and
 * `../blocker.ts` imports this file (the reverse import would be a cycle).
 *
 * Wider than the ask because an answer can legitimately be a paragraph ("use the
 * staging registry at …, the token is in 1Password under …"), and it is composed
 * into a raw task's `ask`, which is capped at `RAW_TASK_ASK_MAX` — an order of
 * magnitude above this, so the composition can never be the thing that truncates.
 */
export const BLOCKER_ANSWER_MAX = 4_000;
/**
 * A note's cap, DERIVED so `<label> — <note>` can never exceed the stored answer's
 * own cap: the composition must not be the thing that a `blockers.answer` parse
 * refuses.
 */
export const BLOCKER_CHOICE_NOTE_MAX =
  BLOCKER_ANSWER_MAX - BLOCKER_OPTION_LABEL_MAX - NOTE_SEPARATOR.length;

/**
 * The ONE sentence a "done" report stands for (#6679 slice B): the person says they did
 * the guidance steps shown on the blocker. Composed by the SERVER, like a choice's label,
 * so the stored answer and the audit row cannot be told apart from what was clicked, and
 * no client writes it. The steps themselves ride into the ask beside it
 * (`blockerAnswerAsk`), which is what lets the judge read "done" as "these steps, done".
 * English on purpose: it is read by a model and an operator, never rendered as UI copy.
 */
export const BLOCKER_DONE_REPORT_ANSWER = 'Done: I followed the steps shown on this blocker.';
/** Joins the done sentence to the person's own words, when they add any. */
const DONE_NOTE_SEPARATOR = ' My note: ';

const OptionKeySchema = z
  .string()
  .max(BLOCKER_OPTION_KEY_MAX)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'a choice key is a lowercase slug: a-z, 0-9, `_` or `-`');

const BlockerOptionSchema = z.object({
  key: OptionKeySchema,
  label: z.string().trim().min(1).max(BLOCKER_OPTION_LABEL_MAX),
  /**
   * The choice is incomplete without words — "split it", where the asker still has
   * to say WHICH slices. A pick of it with no note is refused at the answer door.
   */
  note_required: z.boolean().default(false),
});
export type BlockerOption = z.infer<typeof BlockerOptionSchema>;

const optionMembers = {
  version: z.literal(BLOCKER_OPTIONS_VERSION),
  choices: z.array(BlockerOptionSchema).min(BLOCKER_OPTIONS_MIN).max(BLOCKER_OPTIONS_MAX),
};

/** A key names ONE choice — a duplicate would make a pick ambiguous. */
function uniqueKeys(options: { choices: readonly { key: string }[] }, ctx: z.RefinementCtx): void {
  const seen = new Set<string>();
  for (const [i, choice] of options.choices.entries()) {
    if (seen.has(choice.key)) {
      ctx.addIssue({
        code: 'custom',
        message: `choice key "${choice.key}" appears twice — a key names one choice`,
        path: ['choices', i, 'key'],
      });
    }
    seen.add(choice.key);
  }
}

/** The stored shape, parsed at READ (`normalizeBlocker`). */
export const BlockerOptionsSchema = z.object(optionMembers).superRefine(uniqueKeys);
export type BlockerOptions = z.infer<typeof BlockerOptionsSchema>;

/**
 * What a raiser may WRITE — the read shape plus the credential-shape scan
 * `BlockerNeedsWriteSchema` runs, for the same reason: the labels are plaintext,
 * projected on every `blocker list` and composed into the raw task an answer
 * mints. Not folded into the read, so a scan that hardens later never makes a
 * stored row unreadable. The refusal names the TAG and never the text.
 */
export const BlockerOptionsWriteSchema = z.object(optionMembers).superRefine((options, ctx) => {
  uniqueKeys(options, ctx);
  const shapes = secretShapesInJson(options);
  if (shapes.length === 0) return;
  ctx.addIssue({
    code: 'custom',
    message: `refused: a choice carries a credential-shaped string (${shapes.join(', ')}). Options are shown on every \`blocker list\` and composed into the raw task an answer mints.`,
    path: ['choices'],
  });
});
/** What a raiser hands the write seam — `note_required` may be omitted. */
export type BlockerOptionsDraft = z.input<typeof BlockerOptionsWriteSchema>;

/**
 * MODEL-WRITTEN labels → options, or `null` (#6406). The scout brain and the triage
 * judge may offer choices beside a question. Their words are untrusted, so this
 * adjudicates them and NEVER throws: a bad offer costs the options, never the
 * question, which still reaches a human in prose exactly as before.
 *
 * - `unknown` in: the value arrives off a structured-output parse or a wire event,
 *   and anything but an array of strings answers `null`.
 * - Trimmed. Empty labels and case-insensitive duplicates are dropped.
 * - Keys are POSITIONAL (`choice_1`..`choice_N`): the model never authors a key, so
 *   no model spelling can break the slug rule or collide.
 * - `null` when fewer than {@link BLOCKER_OPTIONS_MIN} or more than
 *   {@link BLOCKER_OPTIONS_MAX} labels survive, when a label is over
 *   {@link BLOCKER_OPTION_LABEL_MAX}, or when the write schema refuses the result
 *   (the credential-shape scan). Too many is refused rather than truncated: a cut
 *   list would offer a pick the model never framed.
 * - `note_required` is false: "Other" already covers a choice that needs words.
 */
export function blockerOptionsFromLabels(labels: unknown): BlockerOptions | null {
  if (!Array.isArray(labels) || !labels.every((l) => typeof l === 'string')) return null;
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const raw of labels as readonly string[]) {
    const label = raw.trim();
    if (label === '' || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    kept.push(label);
  }
  const parsed = BlockerOptionsWriteSchema.safeParse({
    version: BLOCKER_OPTIONS_VERSION,
    choices: kept.map((label, i) => ({ key: `choice_${String(i + 1)}`, label })),
  });
  return parsed.success ? parsed.data : null;
}

/**
 * A raiser's OFFERED choices against the kind it classified (#6406): the options to
 * store, or the refusal code to record. Choices belong only on a `decision_needed`
 * ask (the draft gate refuses them anywhere else), so on another kind they are refused
 * by name rather than stored. Absent or null offers nothing and refuses nothing. The
 * scout box and the mothership's ingest both call this, so the two cannot disagree.
 */
export function adjudicateBlockerChoices(
  kind: string,
  raw: unknown,
): { readonly options?: BlockerOptions; readonly refusal?: string } {
  if (raw === undefined || raw === null) return {};
  // `BlockerKind.DecisionNeeded`, spelled: `../blocker.ts` imports this file, not the reverse.
  if (kind !== 'decision_needed') return { refusal: `choices:not_on_${kind}` };
  const options = blockerOptionsFromLabels(raw);
  return options === null ? { refusal: 'choices:invalid' } : { options };
}

// ── the answer: prose, or a pick ──────────────────────────────────────────────

/**
 * The answer members EVERY door accepts — the REST body and the MCP verb spread
 * these and apply {@link blockerAnswerRefinement}, so the two doors cannot drift
 * into different contracts. Exactly one of `answer` (prose, the "Other" answer)
 * or `choice` (a key of the row's options); `note` only beside a `choice`.
 */
export const BlockerAnswerMembers = {
  answer: z
    .string()
    .min(1)
    .max(BLOCKER_ANSWER_MAX)
    .optional()
    .describe(
      'What was missing, supplied, in words. Carried VERBATIM into the raw task this mints and ' +
        'stored on the blocker, so supply the LOCATION of a credential and never the ' +
        'credential itself: "the npm token is in the vault as NPM_TOKEN", not the token. ' +
        'A secret pasted here is durable in three places and cannot be unsent. Or send `choice`.',
    ),
  choice: OptionKeySchema.optional().describe(
    "A key from the blocker's `options` (`blocker list`), instead of `answer`.",
  ),
  done: z
    .literal(true)
    .optional()
    .describe('The `guidance` steps are done (answered like words); not with `answer`/`choice`.'),
  note: z
    .string()
    .min(1)
    .max(BLOCKER_CHOICE_NOTE_MAX)
    .optional()
    .describe('Words completing a `choice` (required when `note_required`) or a `done`.'),
  /**
   * The model the TRIAGE JUDGE decides this answer's ask on (#6521) — names a key,
   * never carries one.
   *
   * Admitted on every arm that leaves an ask a judge reads, which is THREE: the ask the
   * answer FILES, the parked ask it REOPENS, and the in-flight ask an early answer is
   * HELD for. Refused on the two arms where answering resumes existing work and no
   * judgement happens — a blocker whose linked task is still LIVE (a terminal one falls
   * through to the filed arm and keeps the pin) and a held task's resume.
   *
   * It reaches the JUDGE and stops there: the tasks triage mints from the ask carry no
   * `envelope.llm` (#5117's epic decision, pinned by a test).
   */
  llm: TaskLlmPreferenceSchema.optional().describe(
    "Optional: which of the account's own keys (`billing keys_list`) the triage judge " +
      "decides this answer on. Omit for its usual model. Never changes the work's model.",
  ),
};

/** The one answer-shape sentence, for a door that can only state one (the MCP arm's `refine`). */
export const BLOCKER_ANSWER_SHAPE =
  'send exactly one of `answer` (words), `choice` (an option key from `blocker list`) or `done: true`; `note` only beside a `choice` or `done`';

/** Exactly one of `answer` / `choice` / `done`, and a `note` only beside a `choice` or `done`. */
export function blockerAnswerShapeProblem(input: {
  answer?: string | undefined;
  choice?: string | undefined;
  done?: true | undefined;
  note?: string | undefined;
}): { path: 'answer' | 'note'; message: string } | null {
  const given = [input.answer, input.choice, input.done].filter((v) => v !== undefined).length;
  if (given !== 1) {
    return {
      path: 'answer',
      message: 'send exactly one of `answer` (words), `choice` (an option key) or `done: true`',
    };
  }
  if (input.note !== undefined && input.choice === undefined && input.done === undefined) {
    return {
      path: 'note',
      message:
        '`note` completes a `choice` or a `done` report; with a prose `answer`, put the words in the answer',
    };
  }
  return null;
}

/** {@link blockerAnswerShapeProblem} as a Zod refinement — the REST body's form. */
export function blockerAnswerRefinement(
  input: Parameters<typeof blockerAnswerShapeProblem>[0],
  ctx: z.RefinementCtx,
): void {
  const problem = blockerAnswerShapeProblem(input);
  if (problem !== null)
    ctx.addIssue({ code: 'custom', message: problem.message, path: [problem.path] });
}

/** A parsed answer, as a door hands it to the control: the words or the pick, and the judge's pin. */
export type BlockerAnswerReply =
  | { readonly kind: 'prose'; readonly answer: string }
  | { readonly kind: 'choice'; readonly choice: string; readonly note: string | null }
  /** The guidance steps are done (#6679 slice B), with the person's own words if any. */
  | { readonly kind: 'done'; readonly note: string | null };

/** What a door hands the control: the words or the pick, and the judge's pin. */
export type BlockerAnswerInput = {
  readonly reply: BlockerAnswerReply;
  /** The judge's model for this answer's ask (#6521); absent = the account's own route. */
  readonly llm?: TaskLlmPreference;
};

/**
 * Fold the flat, refined members into the union the control takes.
 *
 * An `llm` that NAMES NOTHING is dropped here, not stored. All three axes of
 * `TaskLlmPreferenceSchema` are optional, so `"llm": {}` parses — and stored it would be
 * a pin that boosts no key, make this ask "pinned" to `pinOfBatch`, and DISAGREE with a
 * real pin on a sibling ask, costing that batch the pick a person actually made. The
 * absent-vs-empty distinction is one a wire shape should never carry (#6521).
 */
export function blockerAnswerInputOf(input: {
  answer?: string | undefined;
  choice?: string | undefined;
  done?: true | undefined;
  note?: string | undefined;
  llm?: TaskLlmPreference | undefined;
}): BlockerAnswerInput {
  const pin = input.llm !== undefined && llmPinNamesSomething(input.llm) ? { llm: input.llm } : {};
  if (input.choice !== undefined) {
    return { reply: { kind: 'choice', choice: input.choice, note: input.note ?? null }, ...pin };
  }
  if (input.done === true) return { reply: { kind: 'done', note: input.note ?? null }, ...pin };
  // Unreachable past `blockerAnswerRefinement`; a caller that skipped it fails here
  // rather than answering with an empty string.
  if (input.answer === undefined) throw new Error(BLOCKER_ANSWER_SHAPE);
  return { reply: { kind: 'prose', answer: input.answer }, ...pin };
}

/** Why a pick could not become an answer — each names the fix. */
export type BlockerChoiceRefusal =
  | { kind: 'no_options' }
  /** A `done` report on a row that shows no steps: there is nothing it could mean. */
  | { kind: 'no_steps' }
  /**
   * A `done` report on a DECISION (#6679 slice C): its steps help a person decide, so
   * "done" carries no decision and the judge would read an empty answer as one.
   */
  | { kind: 'decision_needs_answer' }
  | { kind: 'unknown_choice'; choice: string; offered: readonly string[] }
  | { kind: 'note_required'; choice: string };

/**
 * The pure verdict: an answer input against the row's options → the answer TEXT
 * to store, or why not. Prose passes through untouched, on every row. A pick
 * becomes its label, plus ` — <note>` when one was given. The client never sends
 * a label for the server to trust; the row's own label is the one written.
 */
export function resolveBlockerAnswer(
  row: {
    readonly options: BlockerOptions | null;
    readonly guidance: { readonly kind: string } | null;
    /** The blocker's kind; a DECISION refuses a done report. Absent reads as work. */
    readonly kind?: string;
  },
  input: BlockerAnswerReply,
): { kind: 'ok'; answer: string } | BlockerChoiceRefusal {
  const { options } = row;
  if (input.kind === 'prose') return { kind: 'ok', answer: input.answer };
  // "Done" means the STEPS this row shows, so a row showing none refuses it rather than
  // filing a report the judge could not read. Steps are write-once (null -> value under a
  // CAS on `guidance IS NULL`), so a read that sees them stays true at the answer's commit.
  if (input.kind === 'done') {
    if (row.guidance?.kind !== 'steps') return { kind: 'no_steps' };
    if (row.kind === 'decision_needed') return { kind: 'decision_needs_answer' };
    return {
      kind: 'ok',
      answer:
        input.note === null
          ? BLOCKER_DONE_REPORT_ANSWER
          : `${BLOCKER_DONE_REPORT_ANSWER}${DONE_NOTE_SEPARATOR}${input.note}`,
    };
  }
  if (options === null) return { kind: 'no_options' };
  const chosen = options.choices.find((c) => c.key === input.choice);
  if (chosen === undefined) {
    return {
      kind: 'unknown_choice',
      choice: input.choice,
      offered: options.choices.map((c) => c.key),
    };
  }
  if (chosen.note_required && input.note === null) {
    return { kind: 'note_required', choice: input.choice };
  }
  return {
    kind: 'ok',
    answer: input.note === null ? chosen.label : `${chosen.label}${NOTE_SEPARATOR}${input.note}`,
  };
}

/** One sentence per refusal — the REST 422 detail and the MCP tool error both read it. */
export function blockerChoiceRefusalMessage(refusal: BlockerChoiceRefusal): string {
  switch (refusal.kind) {
    case 'no_options':
      return 'no_options: this blocker offers no choices — answer it in words with `answer`.';
    case 'decision_needs_answer':
      return 'decision_needs_answer: this blocker asks for a decision, so "done" says nothing; send the choice in words with `answer`. Nothing was changed.';
    case 'no_steps':
      return 'no_steps: this blocker shows no guidance steps to report done; answer it in words with `answer`. Nothing was changed.';
    case 'unknown_choice':
      return `unknown_choice: "${refusal.choice}" is not one of this blocker's choices (${refusal.offered.join(', ')}). Nothing was changed.`;
    case 'note_required':
      return `note_required: the choice "${refusal.choice}" needs words to be complete — send them as \`note\`. Nothing was changed.`;
  }
}
